import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PreboardingFormStatus } from '@hygge/shared';
import {
  usePreboardingForm,
  useUpdatePreboardingForm,
} from '@/hooks/use-preboarding-forms';
import { Link } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { usePermission } from '@/lib/permissions';
import { SendInvitationDialog } from '../invitations/send-invitation-dialog';
import { WIZARD_STEPS } from './wizard-steps';

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'text-muted-foreground border-border bg-muted/50',
  PUBLISHED: 'text-green-600 border-green-600/30 bg-green-600/5',
  ARCHIVED: 'text-amber-600 border-amber-600/30 bg-amber-600/5',
};

export function PreboardingFormWizard({ formId }: { formId: string }) {
  const { data: form, isLoading, error } = usePreboardingForm(formId);
  const updateMutation = useUpdatePreboardingForm();
  const [activeIndex, setActiveIndex] = useState(0);
  // Hooks must run unconditionally — keep this above the loading/error early
  // returns so the hook order stays stable across renders.
  const canSend = usePermission('forms.send');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-24 text-center">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-muted-foreground">This form could not be loaded.</p>
        <Link to="/settings/preboarding-forms" className="text-sm text-hygge hover:underline">
          Back to forms
        </Link>
      </div>
    );
  }

  const activeStep = WIZARD_STEPS[activeIndex];
  const StepComponent = activeStep.Component;

  const isPublished = form.status === 'PUBLISHED';
  const setStatus = (status: PreboardingFormStatus) =>
    updateMutation.mutate({ id: form.id, input: { status } });

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header — the Settings layout already renders a "Back to …" link above. */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">
                {form.name}
              </h1>
              <Badge variant="outline" className={cn('text-[11px]', STATUS_BADGE[form.status])}>
                {form.status}
              </Badge>
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Step {activeIndex + 1} of {WIZARD_STEPS.length} — {activeStep.caption}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canSend && <SendInvitationDialog formId={form.id} isPublished={isPublished} />}
            <Button
              variant={isPublished ? 'outline' : 'default'}
              onClick={() => setStatus(isPublished ? 'DRAFT' : 'PUBLISHED')}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isPublished ? 'Move to draft' : 'Publish form'}
            </Button>
          </div>
        </div>
      </div>

      {/* Step tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-6" aria-label="Form builder steps">
          {WIZARD_STEPS.map((step, index) => (
            <button
              key={step.id}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={cn(
                'relative flex items-center gap-2 pb-3 text-sm font-medium transition-colors cursor-pointer',
                index === activeIndex
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-[11px]',
                  index === activeIndex ? 'bg-hygge/10 text-hygge' : 'bg-muted text-muted-foreground',
                )}
              >
                {index + 1}
              </span>
              {step.label}
              {index === activeIndex && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-hygge rounded-t-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Step content */}
      <StepComponent form={form} />
    </div>
  );
}
